// dashboard/Dashboard.jsx
import { useState, useEffect } from "react";
import { useTheme, useMediaQuery, Box, Container, Fade, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/authContext";
import Sidebar from "./components/Sidebar";
import MobileDrawer from "./components/MobileDrawer";
import Topbar from "./components/Topbar";
import Overview from "./components/Overview";
import CoursesInProgress from "./components/CoursesInProgress";
import AllCourses from "./components/AllCourses";
import TelegramSignals from "./components/TelegramSignals";
import MySubscriptions from "./components/MySubscriptions";
import Wallet from "./components/Wallet";
import Transactions from "./components/Transactions";
import Notifications from "./components/Notifications";
import Profile from "./components/Profile";
import Support from "./components/Support";
import CustomLoader from "./components/CustomLoader";

// Placeholder components for sections not yet built
const MyAccess = () => (
  <Box sx={{ py: 4, textAlign: 'center' }}>
    <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a2e' }}>My Access</Typography>
    <Typography variant="body2" sx={{ color: '#8a8a9a', mt: 1 }}>Your access permissions and roles</Typography>
  </Box>
);

const LearningProgress = () => (
  <Box sx={{ py: 4, textAlign: 'center' }}>
    <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a2e' }}>Learning Progress</Typography>
    <Typography variant="body2" sx={{ color: '#8a8a9a', mt: 1 }}>Track your learning journey</Typography>
  </Box>
);

const SubscriptionPlans = () => (
  <Box sx={{ py: 4, textAlign: 'center' }}>
    <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a2e' }}>Subscription Plans</Typography>
    <Typography variant="body2" sx={{ color: '#8a8a9a', mt: 1 }}>Choose your perfect plan</Typography>
  </Box>
);

const RenewSubscription = () => (
  <Box sx={{ py: 4, textAlign: 'center' }}>
    <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a2e' }}>Renew Subscription</Typography>
    <Typography variant="body2" sx={{ color: '#8a8a9a', mt: 1 }}>Extend your subscription</Typography>
  </Box>
);

const DepositFunds = () => (
  <Box sx={{ py: 4, textAlign: 'center' }}>
    <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a2e' }}>Deposit Funds</Typography>
    <Typography variant="body2" sx={{ color: '#8a8a9a', mt: 1 }}>Add funds to your wallet</Typography>
  </Box>
);

const PaymentHistory = () => (
  <Box sx={{ py: 4, textAlign: 'center' }}>
    <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a2e' }}>Payment History</Typography>
    <Typography variant="body2" sx={{ color: '#8a8a9a', mt: 1 }}>View all your payments</Typography>
  </Box>
);

const ForexTools = () => (
  <Box sx={{ py: 4, textAlign: 'center' }}>
    <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a2e' }}>Forex Tools</Typography>
    <Typography variant="body2" sx={{ color: '#8a8a9a', mt: 1 }}>Professional trading tools</Typography>
  </Box>
);

const MyTools = () => (
  <Box sx={{ py: 4, textAlign: 'center' }}>
    <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a2e' }}>My Tools / Licenses</Typography>
    <Typography variant="body2" sx={{ color: '#8a8a9a', mt: 1 }}>Manage your tools and licenses</Typography>
  </Box>
);

const ReferralProgram = () => (
  <Box sx={{ py: 4, textAlign: 'center' }}>
    <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a2e' }}>Referral Program</Typography>
    <Typography variant="body2" sx={{ color: '#8a8a9a', mt: 1 }}>Invite and earn rewards</Typography>
  </Box>
);

const ReferralEarnings = () => (
  <Box sx={{ py: 4, textAlign: 'center' }}>
    <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a2e' }}>My Referral Earnings</Typography>
    <Typography variant="body2" sx={{ color: '#8a8a9a', mt: 1 }}>Track your referral income</Typography>
  </Box>
);

const HelpSupport = () => (
  <Box sx={{ py: 4, textAlign: 'center' }}>
    <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a2e' }}>Help & Support</Typography>
    <Typography variant="body2" sx={{ color: '#8a8a9a', mt: 1 }}>Find answers and get help</Typography>
  </Box>
);

const ContactSupport = () => (
  <Box sx={{ py: 4, textAlign: 'center' }}>
    <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a2e' }}>Contact Support</Typography>
    <Typography variant="body2" sx={{ color: '#8a8a9a', mt: 1 }}>Reach out to our team</Typography>
  </Box>
);

const Security = () => (
  <Box sx={{ py: 4, textAlign: 'center' }}>
    <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a2e' }}>Security</Typography>
    <Typography variant="body2" sx={{ color: '#8a8a9a', mt: 1 }}>Manage your security settings</Typography>
  </Box>
);

const Settings = () => (
  <Box sx={{ py: 4, textAlign: 'center' }}>
    <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a2e' }}>Settings</Typography>
    <Typography variant="body2" sx={{ color: '#8a8a9a', mt: 1 }}>Customize your preferences</Typography>
  </Box>
);

const Dashboard = () => {
  const [active, setActive] = useState("Overview");
  const [collapsed, setCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user, isAuthenticated, isInitialized, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isInitialized) return;
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    if (isAuthenticated) {
      const timer = setTimeout(() => setIsLoading(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, isInitialized, navigate]);

  if (!isInitialized || isLoading || loading) {
    return <CustomLoader message={!isInitialized ? "Authenticating..." : "Loading Dashboard..."} />;
  }

  if (!isAuthenticated) return null;

  // Render content based on active section
  const renderContent = () => {
    switch(active) {
      case "Overview":
        return <Overview />;
      case "MyCourses":
        return <CoursesInProgress />;
      case "AllCourses":
        return <AllCourses />;
      case "TelegramSignals":
        return <TelegramSignals />;
      case "MySubscription":
        return <MySubscriptions />;
      case "Wallet":
        return <Wallet />;
      case "Transactions":
        return <Transactions />;
      case "Notifications":
        return <Notifications />;
      case "Profile":
        return <Profile />;
      case "HelpSupport":
        return <Support />;
      case "MyAccess":
        return <MyAccess />;
      case "LearningProgress":
        return <LearningProgress />;
      case "SubscriptionPlans":
        return <SubscriptionPlans />;
      case "RenewSubscription":
        return <RenewSubscription />;
      case "DepositFunds":
        return <DepositFunds />;
      case "PaymentHistory":
        return <PaymentHistory />;
      case "ForexTools":
        return <ForexTools />;
      case "MyTools":
        return <MyTools />;
      case "ReferralProgram":
        return <ReferralProgram />;
      case "ReferralEarnings":
        return <ReferralEarnings />;
      case "ContactSupport":
        return <ContactSupport />;
      case "Security":
        return <Security />;
      case "Settings":
        return <Settings />;
      default:
        return <Overview />;
    }
  };

  return (
    <Box sx={{ display: "flex", bgcolor: "#f5f6fa", minHeight: "100vh" }}>
      {isMobile ? (
        <MobileDrawer active={active} setActive={setActive} />
      ) : (
        <Sidebar 
          active={active}
          setActive={setActive}
          collapsed={collapsed}
          setCollapsed={setCollapsed}
        />
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minHeight: "100vh",
          overflowX: 'hidden',
          marginLeft: isMobile ? 0 : collapsed ? '80px' : '260px',
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          width: isMobile ? '100%' : `calc(100% - ${collapsed ? 80 : 260}px)`
        }}
      >
        <Topbar user={user} isMobile={isMobile} toggleSidebar={() => setCollapsed(prev => !prev)} />

        <Container maxWidth="xl" sx={{ mt: 3, pb: 5, px: isMobile ? 2 : 3 }}>
          <Fade in={true} timeout={500}>
            <Box>
              {renderContent()}
            </Box>
          </Fade>
        </Container>
      </Box>
    </Box>
  );
};

export default Dashboard;